<template>
  <div class="mic-selector">
    <el-tooltip
      class="item"
      effect="dark"
      :content="shortcutKeyPrompt"
      placement="top-start"
    >
      <div
        class="mic-btn"
        @click="handleCameraClick"
        tabindex="0"
        aria-label="视频"
        @keydown.enter="handleCameraClick"
      >
        <img
          v-if="isCameraLoading"
          src="@saas/pages/assets/images/live/loading.png"
          alt=""
          class="loading-icon"
        />
        <img
          v-else-if="showCloseIcon"
          class="mic-icon"
          :src="require('@saas/pages/assets/images/live/video-close.png')"
          alt="视频"
        />
        <img
          v-else
          class="mic-icon"
          :src="require('@saas/pages/assets/images/live/video.png')"
          alt="视频"
        />

        <span class="mic-label">视频</span>
      </div>
    </el-tooltip>

    <el-popover
      v-model="popoverVisible"
      placement="bottom-start"
      trigger="click"
      popper-class="mic-popover"
      :visible-arrow="false"
      :append-to-body="true"
      @hide="onHidePopover"
      @show="onShowPopover"
    >
      <div class="popover-content" ref="popoverContent">
        <div class="group-title">选择摄像头</div>
        <ul class="device-list">
          <li
            v-for="item in cameraListOptions"
            :key="item.deviceId"
            :class="[
              'device-item',
              {
                selected: selectCamera.deviceId === item.deviceId,
                disabled: item.disabled,
              },
            ]"
            @click="!item.disabled && handleSelectCamera(item)"
            :tabindex="item.disabled ? -1 : 0"
            :aria-label="item.deviceName"
          >
            <span class="checkmark">
              <img
                v-if="selectCamera.deviceId === item.deviceId"
                src="@saas/pages/assets/images/live/selected.png"
                alt=""
              />
            </span>
            <div class="label ellipsis">{{ item.deviceName }}</div>
          </li>
        </ul>
        <div class="divider"></div>
        <div
          class="bottom-setting"
          @click="handleSetting"
          tabindex="0"
          aria-label="设置"
          @keydown.enter="handleSetting"
        >
          <span class="checkmark"> </span>
          <span class="setting-label">设置</span>
        </div>
      </div>
      <div
        slot="reference"
        class="arrow-btn"
        @click="handleClickMore"
        tabindex="0"
        aria-label="展开视频频选项"
        @keydown.enter.stop="popoverVisible = !popoverVisible"
      >
        <i
          :class="isScreenShare ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"
          class="arrow-icon"
        ></i>
      </div>
    </el-popover>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import handleSelf from "../mixins/handle-self";
import ScreenShare from "../mixins/screen-share";
import { uploadLiveLog } from "@saas/components/src/utils/log";
import { debounce } from "@saas/components/src/utils/debounce-async";
export default {
  name: "MicSelector",
  mixins: [handleSelf, ScreenShare],
  data() {
    return {
      popoverVisible: false,
      isCameraLoading: false,
      isSelectCameraLoading: false,
    };
  },
  watch: {
    selfMicVolume: {
      handler(val) {
        clearTimeout(this.speakTimer);
        this.speakTimer = setTimeout(() => {
          this.isSpeaking = false;
        }, 1000);
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState("live", [
      "liveService",
      "currentCameraDevice",
      "userStatus",
      "cameraList",
      "screenShareStatus",
      "deviceSwtich",
      "videoPermission",
    ]),

    cameraListOptions() {
      return [...this.cameraList, { deviceId: "disabled", deviceName: "禁用" }];
    },
    shortcutKeyPrompt() {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const shortcut = isMac ? "⌘+Shift+V" : "Ctrl+Shift+V";

      if (!this.showCloseIcon) {
        return `关闭摄像头 (${shortcut})`;
      } else {
        return `打开摄像头 (${shortcut})`;
      }
    },
    selectCamera() {
      if (
        this.deviceSwtich.camera === "off" ||
        this.userStatus.is_up_stage !== 1
      ) {
        return { deviceId: "disabled" };
      } else {
        return this.currentCameraDevice;
      }
    },
    showCloseIcon() {
      return (
        this.userStatus.is_up_stage !== 1 || this.userStatus.video_state !== 1
      );
    },
    // 是否在共享(共享端)
    isScreenShare() {
      return ["start", "pause"].includes(this.screenShareStatus);
    },
  },
  mounted() {
    // 使用 capture 模式确保事件能被正确捕获
    document.addEventListener("keydown", this.shortcutKey, true);
    if (window.ipcRenderer) {
      window.ipcRenderer.on("shortcut-camera-toggle", this.handleCameraClick);
    }
  },
  beforeDestroy() {
    // 移除事件监听器时也要指定 capture 模式
    document.removeEventListener("keydown", this.shortcutKey, true);
    if (window.ipcRenderer) {
      window.ipcRenderer.removeListener(
        "shortcut-camera-toggle",
        this.handleCameraClick
      );
    }
  },
  methods: {
    ...mapMutations("live", [
      "setMicrophoneList",
      "setSpeakerList",
      "setCameraList",
      "setShowSet",
      "setDeviceSwtich",
      "setCurrentCameraDevice",
    ]),
    handleCameraClick: debounce(
      function () {
        if (this.isCameraLoading) {
          return;
        }
        this.isCameraLoading = true;
        uploadLiveLog({
          message: "用户点击摄像头开关",
          tag: "userAction",
          extra: {
            component: "CameraSelector",
            action: "click_select_camera_toggle",
            currentVideoState: this.userStatus.video_state,
            isUpStage: this.userStatus.is_up_stage,
          },
        });

        // // 可扩展为静音/取消静音
        // if (this.userStatus.is_up_stage !== 1) {
        //   this.$darkMessage.error("上台后，才能开启音视频功能");
        //   return;
        // }
        const isMute = !this.showCloseIcon;
        this.handleSelfCamera(isMute)
          .then(() => {
            this.isCameraLoading = false;
          })
          .catch((error) => {
            this.isCameraLoading = false;
            console.error("操作自己摄像头失败:", error);
          });
      },
      300,
      true
    ),
    handleSelectCamera: debounce(
      function (device) {
        this.popoverVisible = false;
        if (this.isSelectCameraLoading) {
          return;
        }
        this.isSelectCameraLoading = true;
        uploadLiveLog({
          message: "用户切换摄像头",
          extra: {
            component: "CameraSelector",
            action: "click_select_camera_select",
            deviceId: device.deviceId,
            deviceName: device.deviceName,
            isDisabled: device.deviceId === "disabled",
          },
        });

        if (device.deviceId === "disabled") {
          this.handleSelfCamera(true)
            .then(() => {
              this.isSelectCameraLoading = false;
              this.setDeviceSwtich({
                camera: "off",
              });
            })
            .catch((error) => {
              this.isSelectCameraLoading = false;
              console.error("禁用自己摄像头失败:", error);
            });
        } else {
          // if (this.userStatus.is_up_stage !== 1) {
          //   this.$darkMessage.error("上台后，才能开启音视频功能");
          //   return;
          // }
          // if (!this.videoPermission) {
          //   this.$darkMessage.error("打开系统权限后重试");
          //   return;
          // }
          // //
          // if (this.userStatus.is_send_video !== 1) {
          //   // 没有权限开启
          //   this.$darkMessage.error("您被老师禁止打开摄像头");
          //   return;
          // }

          this.handleSelfCamera(false)
            .then(() => {
              this.setCurrentCameraDevice(device);
              this.isSelectCameraLoading = false;
              this.setDeviceSwtich({
                camera: "on",
              });
            })
            .catch((error) => {
              this.isSelectCameraLoading = false;
              console.error("切换自己摄像头失败:", error);
            });
        }
        this.popoverVisible = false;
      },
      300,
      true
    ),
    handleSetting() {
      uploadLiveLog({
        message: "用户点击摄像头设置",
        extra: {
          component: "CameraSelector",
          action: "click_select_camera_setting",
        },
      });

      // 打开设置弹窗或页面
      this.setShowSet({
        visible: true,
        initActive: "video",
      });
      this.popoverVisible = false;
    },
    handleClickMore() {
      uploadLiveLog({
        message: "用户点击摄像头展开选项",
        extra: {
          component: "CameraSelector",
          action: "click_select_camera_more",
        },
      });

      if (this.liveService) {
        this.liveService.getCameraList().then((res) => {
          const list = res.filter((item) => item.deviceId) || [];
          this.setCameraList(list);
          if (list.length === 0) {
            // 如果没有摄像头
            this.setVideoPermission(false);
          }
        });
      } else {
        console.error("handleClickMore ———— liveservice 不存在");
      }
    },
    onHidePopover() {
      this.popoverVisible = false;
    },
    onShowPopover() {
      this.popoverVisible = true;
    },
    shortcutKey(event) {
      // 检查 Command+Shift+V (Mac) 或 Ctrl+Shift+V (Windows/Linux)
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        (event.key === "v" || event.key === "V")
      ) {
        event.preventDefault(); // 阻止默认行为
        this.handleCameraClick();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mic-selector {
  display: flex;
  align-items: center;
  background: transparent;
}
.mic-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  outline: none;
  border-radius: 8px;
  // padding: 4px 8px 0 8px;
}
.mic-icon {
  width: 20px;
  height: 20px;
  // margin-bottom: 2px;
}
.mic-label {
  font-weight: 400;
  font-size: 11px;
  color: #fff;
  margin-top: 1px;
}
.arrow-btn {
  width: 16px;
  height: 42px;
  border-radius: 6px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 10px;
  margin-left: 3px;
  &:hover {
    background: #27272a;
  }
}
.arrow-icon {
  font-size: 12px;
  color: #c9cdd4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popover-content {
}
.group-title {
  font-weight: 400;
  font-size: 12px;
  color: #86909c;
}
.device-list {
  list-style: none;
  // margin: 0 0 8px 0;
  // padding: 0 16px;
}
.device-item {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  height: 22px;
  font-weight: 400;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  // transition: background 0.2s;
  margin-top: 12px;
  .label {
    font-size: 14px !important;
  }
  &:hover:not(.disabled),
  &.selected {
    // background: #232329;
  }
  &.selected {
    // font-weight: bold;
    // color: #f54c46;
  }
  &.disabled {
    // color: #666;
    // cursor: not-allowed;
    // background: none;
  }
}
.checkmark {
  width: 22px;
  min-width: 22px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  img {
    width: 18px !important;
    height: 18px !important;
  }
}
.divider {
  height: 1px;
  background: #27272a;
  border-radius: 0px 0px 0px 0px;

  margin: 12px 0;
}
.bottom-setting {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  border-radius: 6px;
  outline: none;
}
.setting-dash {
  width: 16px;
  height: 16px;
  border: 1px dashed #fff;
  border-radius: 4px;
  margin-right: 8px;
  display: inline-block;
}
.setting-label {
  font-size: 15px;
}
</style>
<style>
.mic-popover {
  width: 170px;
  background: #18181b;
  box-shadow: 0px 8px 60px 2px rgba(0, 0, 0, 0.8);
  border-radius: 12px 12px 12px 12px;
  border: 1px solid #27272a;
  padding: 12px;
}
</style>
